import type { Currency } from '../models/Currency';
import type { CancelGoodsDisposition, OrderCancelType } from './FulfillmentAdminClient';
import type { InboundStatus } from '../models/InboundStatus';
import { sgtRangeToInstantBounds } from '../utils/dateUtils';

export enum ShippingProviderType {
  DELEO = 'DELEO',
  FEDEX = 'FEDEX',
  PARXL = 'PARXL',
  YUN = 'YUN_EXPRESS',
}

export enum ShippingStatus {
  PENDING = 'PENDING',
  PICKED_UP = 'PICKED_UP',
  IN_TRANSIT = 'IN_TRANSIT',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  RETURNED = 'RETURNED',
}

export interface UnifiedTrackingEventEntry {
  event: string;
  provider: string;
  providerEventCode: string;
  timestamp: string;
  description?: string;
  displayText: string;
  customerVisible: boolean;
}

export interface UnifiedStatusHistoryEntry {
  status: string;
  provider: string;
  providerStatusCode: string;
  timestamp: string;
}

export interface ShippingData {
  provider: ShippingProviderType;
  providerOrderId: string;
  trackingNumber?: string;
  status: ShippingStatus;
  statusCode?: string;
  statusDescription?: string;
  statusUpdatedAt?: string;
  labelUrl?: string;
  createdAt: string;
  updatedAt: string;
  unifiedStatus?: string;
  unifiedStatusUpdatedAt?: string;
  events?: UnifiedTrackingEventEntry[];
  statusHistory?: UnifiedStatusHistoryEntry[];
}

export interface ParxlTrackingEvent {
  status: string;
  date: string;
  description?: string;
  location?: string;
}

export interface ParxlData {
  plsShipmentReference: string;
  merchantBatchNo?: string;
  merchantOrderNo: string;
  orderDate: string;
  operationHub?: string;
  serviceOption: string;
  latestTrackingStatus: string;
  trackingStatus: ParxlTrackingEvent[];
  weight?: number;
  weightUnit?: string;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: string;
  };
  incoterm?: string;
  containsBattery?: boolean;
  containsDg?: boolean;
  insured?: boolean;
  cod?: boolean;
  expirationDate?: string;
}

export interface YunExpressTrackEvent {
  processTime: string;
  processUtcTime: string;
  processContent: string;
  processCountry: string;
  processProvince: string;
  processCity: string;
  processLocation: string;
  trackNodeCode: string;
  trackNodeDescription?: string;
  podUrl?: string;
}

export interface YunExpressTrackInfo {
  waybillNumber?: string;
  trackingNumber?: string;
  customerOrderNumber?: string;
  productCode?: string;
  productName?: string;
  channelCode?: string;
  checkInTime?: string;
  checkOutTime?: string;
  pickUpTime?: string;
  customerCode?: string;
  originCode?: string;
  destinationCode?: string;
  postalCode?: string;
  actualWeight?: number;
  intervalDay?: number;
  intervalWorkDay?: number;
  lastMileSite?: string;
  lastMileName?: string;
  phoneNumber?: string;
  trackEvents?: YunExpressTrackEvent[];
  podUrl?: string;
  isSignature?: boolean;
}

export interface YunExpressData {
  waybillNumber: string;
  customerOrderNumber?: string;
  lastPackageStatus?: string;
  lastSyncedAt?: string;
  trackInfo?: YunExpressTrackInfo;
}

export interface OpsComment {
  id?: string;
  timestamp?: string;
  operator: string;
  message: string;
}

export interface SourcingShipmentTrack {
  orderNumber: string; // Coupang, Naver, etc. order number - given input to Shipment Tracker
  orderDate: string; // Order date from sourcing site - output from Shipment Tracker
  productName?: string; // Product name from sourcing site (may differ from lineItem.name) - output from Shipment Tracker
  productCost?: number; // Product cost from sourcing site - output from Shipment Tracker
  trackingNumber: string; // Tracking number - output from Shipment Tracker
  deliveryCompanyCd: string; // Delivery company code - output from Shipment Tracker
  inboundArrivalDate?: string; // Inbound arrival date at warehouse - output from Shipment Tracker
  shipmentTrackUrl?: string; // Shipment tracking URL - output from Shipment Tracker
}

export interface LineItem {
  itemId?: string;
  name: string;
  nameKor?: string;
  originalName?: string; // original name in woocommerce
  originalNameKor?: string; // original korean name in woocommerce
  price: number;
  productId: string;
  parentProductId?: string;
  productPack?: string;
  productSize?: string;
  originalSku?: string; // woocommerce product sku, mostly variation sku
  sku: string; // variable product sku
  originalQuantity?: number; // original quantity in woocommerce
  quantity: number; // calculated quantity in oms
  subtotal: number;
  total: number;
  totalTax: number;
  wooCommerceImageUrl?: string;
  sourcingRoute?: string;
  /** Optional server-recorded platform; legacy responses omit it, so purchase order numbers stay text. */
  purchasePlatform?: string | null;

  // ---> deprecated fields, but still used for backward compatibility
  cpOrderNumber?: string; // tracking number in legacy
  cpOrderDate?: string;
  cpProductCost?: number;
  cpInboundArrivalDate?: string;
  cpInvoice?: string;
  // <--- deprecated fields, but still used for backward compatibility

  cpUrl?: string;
  comments?: string;
  toDelete?: boolean;
  deliveryCompanyCd?: string;
  inboundStatus?: InboundStatus;
  barcode?: string;

  sourcingShipmentTrack?: SourcingShipmentTrack;
}

export interface BillingAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
}

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone: string;
}

export interface Consignee {
  name: string;
  phone: string;
  countryCode: string;
  zipcode: string;
  address: string;
  city: string;
  state: string;
}

export interface Customer {
  id: string;
  email: string;
  name: string;
  phone: string;
}

export interface DeleoStatus {
  name: string;
  code: string;
  desc: string;
}

export interface DeleoData {
  deleoOrderId: string;
  deleoTrackingNo?: string;
  deleoStatus?: DeleoStatus;
  inboundTrackingNo?: string;
  orderTrackingList?: any[];
  orderInboundList?: any[];
  orderItemList?: any[];
  wmsInboundList?: any[];
  deleoStockStatusAtCreation?: any[];
}

export interface Order {
  orderId: string;
  orderDate: string;
  orderMonth: string;
  orderDateTime: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  status: string;
  currency: string;
  billing: BillingAddress;
  shipping: ShippingAddress;
  consignee: Consignee;
  customer: Customer;
  shippingMessage: string;
  lineItems: LineItem[];
  totalAmount: number;
  taxAmount?: number;
  discount: number;
  shippingTotal?: number;
  shippingTax?: number;
  paymentMethod?: string;
  paymentMethodTitle?: string;
  transactionId?: string;
  parentOrderId?: string;
  ipAddress?: string;
  userAgent?: string;
  pic?: string;
  // Display name of the gig worker (GigWorks PIC) that claimed this order; set only for GIGWORK-routed orders.
  gigWorker?: string;
  deleoData?: DeleoData;
  opsComments?: OpsComment[];
  nonExistentProduct?: boolean;
  requiresInbound?: boolean;
  auditEntry?: any;
}

export interface OrderSummary {
  orderId: string;
  orderDate: string;
  orderMonth: string;
  orderDateTime: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  status: string;
  consignee: {
    name: string;
    countryCode: string;
    zipcode: string;
    address: string;
    city: string;
    state: string;
  };
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  shippingMessage?: string;
  billingAddress?: OrderAddress;
  shippingAddress?: OrderAddress;
  lineItems: LineItem[];
  totalAmount: number;
  currency: Currency;
  discount: number;
  deleoData?: {
    deleoOrderId: string;
    deleoTrackingNo?: string;
    deleoStatus?: {
      name: string;
      code: string;
      desc: string;
    };
    orderItemList?: Array<{
      orderItemId: string;
      itemId: number;
      itemCd: string;
      itemNm: string;
      quantity: number;
      amt: number;
      centerCd: string;
      orgItemCd: string;
      packageItem: boolean;
      releaseDate: string | null;
      releaseQuantity: number | null;
      remark: string | null;
      status: {
        name: string;
        code: string;
        desc: string;
      };
      itemType: {
        name: string;
        code: string;
        desc: string;
      };
      wmsAsnCd: string | null;
    }>;
    orderTrackingList?: Array<{
      createdBy: string;
      createdOn: string;
      updatedBy: string;
      updatedOn: string;
      shipmentRequestId: string;
      deliveryCompanyCd: {
        name: string;
        code: string;
        desc: string;
      };
      trackingNo: string;
      wmsAsnCd: string | null;
      remark: string | null;
      deleoTrackingNo: string | null;
      orderId: string;
      requestId: string;
    }>;
    orderInboundList?: Array<{
      wmsInboundId: string;
      sellerNm: string | null;
      deliveryCompanyNm: string;
      trackingNo: string;
      itemCd: string;
      quantity: number;
      createdOn: string;
    }>;
    wmsInboundList?: Array<{
      inboundId: string;
      requestDate: string;
      trackingNo: string;
      referenceId: string;
      status: {
        name: string;
        code: string;
        desc: string;
      };
      inboundDate: string;
      deliveryCompanyCd: {
        name: string;
        code: string;
        desc: string;
      };
    }>;
  };
  opsComments?: OpsComment[];
  sourcingRoute?: string;
  cpOrderNumber?: string;
  cpOrderDate?: string;
  coCost?: number;
  cpUrl?: string;
  cpArrivalDate?: string;
  cpInvoice?: string;
  cpProductCost?: number;
  cpInboundArrivalDate?: string; // same as Deleo's Inbound Arrival Date

  nonExistentProduct?: boolean;
  requiresInbound?: boolean;
  pic?: string;
  // Display name of the gig worker (GigWorks PIC) that claimed this order; set only for GIGWORK-routed orders.
  gigWorker?: string;
  // Interim WMS field: temporary holding location entered by the receiving operator. Used until WMS 2.0.
  temporaryLocation?: string;
  auditEntry: AuditHistoryEntryInput;
  shippingData?: ShippingData;
  parxlData?: ParxlData;
  yunExpressData?: YunExpressData;
  shipmentMutation?: ShipmentMutation;
  shipmentSwitch?: ShipmentSwitchRecovery;
}

export interface TrackingInfo {
  deleoTrackingNumber?: string;
  lastMileDeliveryCompany?: string;
  lastMileTrackingNumber?: string;
}

export interface CustomsNotification {
  charged: boolean;
  totalCharge?: number;
  currency?: Currency;
  description?: string;
  manualComments?: string;
}

export interface OrderListParams {
  // Query mode (pick one): single month, or a startDate+endDate range, or status-only.
  // When startDate+endDate are sent, the backend ignores orderMonth.
  orderMonth?: string;
  // YYYY-MM-DD order_date range; both endpoints are inclusive.
  startDate?: string;
  endDate?: string;
  countryCode?: string;
  status?: string;
  // Single order-date filter (YYYY-MM-DD), used with the status query mode.
  orderDate?: string;
  search?: string;
  pic?: string;
  hasOrderNumber?: boolean;
  hasTrackingNumber?: boolean;
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface OrderListResponse {
  orders: OrderSummary[];
  totalCount?: number;
  // Opaque cursor. V2 returns an object ({ pk, sk, gsi... }); legacy returned { orderId, orderMonth }.
  // Treat it as a black box: JSON.stringify and pass straight back as the next request's lastEvaluatedKey.
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface WmsOrderSearchParams {
  trackingNumber?: string;
  inboundTrackingOrderNumber?: string;
}

export interface InboundRequestItem {
  trackingNo: string;
  deliveryCompanyCode: string;
  itemList: Array<{
    code: string;
    quantity: number;
  }>;
}

export interface InboundRequest {
  shippingMessage?: string;
  trackingList: InboundRequestItem[];
  auditEntry: AuditHistoryEntryInput;
}

export interface DeliveryCompany {
  name: string;
  code: string;
  desc: string;
}

export interface InboundResponse {
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
  orderId: string;
  custCd: string;
  shopInfoId: number;
  requestId: string;
  status: {
    name: string;
    code: string;
    desc: string;
  };
  shippingMessage: string | null;
  totalAmt: number;
  shippingFeeAmt: number | null;
  rateVersion: string | null;
  amtUnit: {
    name: string;
    code: string;
    desc: string;
  };
  serviceTypeCd: string | null;
  deliveryPaymentOption: string | null;
  orderDate: string;
  cancelDate: string | null;
  completeFailDate: string | null;
  releaseDate: string | null;
  wmsAsnCd: string | null;
  centerCd: string | null;
  groupKey: string | null;
  exportDeclarationYn: string | null;
  orgOrderId: string | null;
  orgRequestId: string | null;
  deleoTrackingNo: string;
  inboundTrackingNo: string;
  domesticDeliveryCompanyCode: string | null;
  inboundLabelType: {
    name: string;
    code: string;
    desc: string;
  };
  orderType: {
    name: string;
    code: string;
    desc: string;
  };
  sender: {
    addressName: string | null;
    name: string;
    nameEn: string | null;
    phone: string;
    cellphone: string;
    virtualPhoneNo: string | null;
    countryCd: string;
    zipcode: string;
    state: string | null;
    province: string | null;
    city: string;
    street: string;
    address1: string;
    address2: string;
    addressFull: string;
  };
  receiver: {
    addressName: string | null;
    name: string;
    phone: string;
    cellphone: string;
    virtualPhoneNo: string | null;
    email: string;
    countryCd: string;
    zipcode: string;
    state: string;
    province: string | null;
    city: string;
    street: string | null;
    address1: string;
    address2: string | null;
    addressFull: string;
    clearanceInformationType: string | null;
    clearanceInformation: string | null;
  };
  orderItemList: Array<{
    orderItemId: string;
    itemId: number;
    itemCd: string;
    orgItemCd: string;
    itemType: {
      name: string;
      code: string;
      desc: string;
    };
    itemNm: string;
    quantity: number;
    amt: number;
    releaseQuantity: number | null;
    remark: string | null;
    wmsAsnCd: string;
    centerCd: string;
    releaseDate: string | null;
    status: {
      name: string;
      code: string;
      desc: string;
    };
    packageItem: boolean;
  }>;
  orderTrackingList: Array<{
    createdBy: string;
    createdOn: string;
    updatedBy: string;
    updatedOn: string;
    shipmentRequestId: string;
    deliveryCompanyCd: DeliveryCompany;
    trackingNo: string;
    wmsAsnCd?: string;
    remark?: string;
    deleoTrackingNo?: string;
    orderId: string;
    requestId: string;
  }>;
  orderInboundList: Array<{
    wmsInboundId: string;
    sellerNm: string | null;
    deliveryCompanyNm: string | null;
    trackingNo: string;
    itemCd: string;
    quantity: number;
    createdOn: string;
  }>;
  serviceOptionList: any[];
  shopPaymentDate: string | null;
}

export interface StockStatusResponse {
  items: Array<{
    sku: string;
    quantity: number;
  }>;
}

export interface TraceItem {
  status: string;
  detailStatus: string | null;
  eventTime: string;
  empNm: string | null;
  description: string;
  location: string | null;
}

export interface TraceInfoItem {
  deliveryCompanyCode: {
    name: string;
    code: string;
    desc: string;
  };
  domesticDeliveryCompanyCode: string | null;
  trackingNo: string;
  mainTrackingNo: string;
  deleoTrackingNo: string | null;
  origin: string;
  dest: string;
  status: string;
  description: string;
  custCd: string | null;
  custType: string | null;
  deleoTrackList: any | null;
  traceList: TraceItem[];
}

export interface TraceResponse {
  orderId: string;
  status: {
    name: string;
    code: string;
    desc: string;
  };
  traceInfoList: TraceInfoItem[];
}

export interface AuditHistoryEntry {
  updatedData: any; // Could be anything that was sent to update
  operator?: string; // Only present for OPERATOR source
  source: string; // OPERATOR, DELEO, WOOCOMMERCE, etc.
  operation: string; // Operation performed (UPDATEORDER, CREATEORDER, CANCELORDER, etc.)
  updatedTimestamp: number;
  orderId: string;
}

export interface AuditHistoryEntryInput {
  operator: string;
  source: string;
}

export interface ShipmentOperatorRequest {
  reason: string;
}

export interface CreateShipmentRequest extends ShipmentOperatorRequest {
  deliveryProvider?: ShippingProviderType;
}

export interface OrderAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone: string;
  taxId?: string;
  // Saudi National Address short code (4 uppercase letters + 4 digits, e.g. EGDA6366).
  // Required by YunExpress for SA shipments (receiver.short_address). Optional otherwise.
  shortAddressCode?: string;
}

export interface OrderCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
}

export interface OrderUpdateRequest {
  orderMonth?: string;
  orderDateTime?: string;
  countryCode?: string;
  customerId?: string;
  billingAddress?: OrderAddress;
  shippingAddress?: OrderAddress;
  customer?: OrderCustomer;
  shippingMessage?: string;
  lineItems?: LineItem[];
  totalAmount?: number;
  currency?: string;
  discount?: number;
  deleoData?: DeleoData;
  opsComments?: OpsComment[];
  pic?: string;
  nonExistentProduct?: boolean;
  requiresInbound?: boolean;
  // Interim WMS field: temporary holding location. Used until WMS 2.0.
  temporaryLocation?: string;
  shippingData?: Partial<ShippingData>;
  auditEntry: AuditHistoryEntryInput;
}

export interface RefreshShippingStatusResponse {
  orderId: string;
  provider: string;
  previousStatus: string | null;
  newStatus: string | null;
  statusChanged: boolean;
  success: boolean;
  error?: string;
}

export interface CloneOrderRequest {
  originalOrderId: string;
  newOrderId: string;
  newShipmentId?: string;
  newDeleoOrderId?: string;
  deliveryProvider?: ShippingProviderType;
  lineItemQuantityOverrides?: LineItemQuantityOverride[];
  idempotencyKey: string;
  reason: string;
}

export interface LineItemQuantityOverride {
  itemId: string;
  quantity: number;
}

export type ShipmentMutationState =
  | 'PREPARED'
  | 'CANCEL_PENDING'
  | 'CANCEL_FAILED'
  | 'CANCEL_UNKNOWN'
  | 'CANCEL_CONFIRMED'
  | 'CREATE_PENDING'
  | 'CREATE_FAILED'
  | 'CREATE_UNKNOWN'
  | 'COMPLETED'
  | 'RECONCILIATION_REQUIRED';

export interface ShipmentMutation {
  operationId: string;
  idempotencyKey: string;
  type: 'PROVIDER_SWITCH' | 'CLONE';
  state: ShipmentMutationState;
  actor: string;
  reason: string;
  sourceOrderId: string;
  sourceProvider?: ShippingProviderType;
  sourceProviderOrderId?: string;
  targetProvider?: ShippingProviderType;
  targetProviderOrderId?: string;
  startedAt: string;
  updatedAt: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface ShipmentSwitchRecovery {
  operationId: string;
  idempotencyKey: string;
  state: ShipmentMutationState;
  reason: string;
  sourceProvider?: ShippingProviderType;
  sourceProviderOrderId?: string;
  targetProvider?: ShippingProviderType;
  errorCode?: string;
  errorMessage?: string;
  recoveryAllowed: boolean;
}

export interface SwitchShipmentProviderRequest {
  retryFailedCreation?: boolean;
  targetProvider: ShippingProviderType;
  expectedSourceProviderOrderId: string;
  idempotencyKey: string;
  reason: string;
}

export interface SwitchShipmentProviderResponse {
  operationId: string;
  state: ShipmentMutationState;
  order: OrderSummary;
}

export interface MergeOrdersRequest {
  primaryOrderId: string;
  mergeOrderIds: string[];
  auditEntry: AuditHistoryEntryInput;
}

export interface SLAResult {
  status: 'WITHIN_SLA' | 'AT_RISK' | 'BREACHED' | 'COMPLETED';
  targetDays: number;
  elapsedDays: number;
  remainingDays: number;
  orderStatus: string;
}

export interface OrderFulfillmentStatus {
  order: OrderSummary;
  slaResult: SLAResult;
}

export interface FulfillmentSummary {
  withinSLA: number;
  atRisk: number;
  breached: number;
  completed: number;
}

export interface OrderFulfillmentStatusListResponse {
  items: OrderFulfillmentStatus[];
  total: number;
  summary: FulfillmentSummary;
}

/** What the order cancel sends besides the audit entry; the cancel type and asked time are SSD-7450's, both optional. */
export interface OrderCancelInfo {
  /** Per-line disposition contract; see CancelGoodsDisposition for stock effects. */
  lines?: CancelGoodsDisposition[];
  reason: string;
  operator: string;
  cancelType?: OrderCancelType;
  /** ISO UTC; only for a change of mind. */
  customerRequestedAt?: string;
  /** Why a change of mind asked after the lock is cancelled anyway (SSD-7643); the server posts it to the comments. */
  lockOverrideReason?: string;
  /** What to do with each line's goods (SSD-7643); the server posts it to the comments. */
  goodsNote?: string;
}

const listOrdersPageRequests = new Map<string, Promise<OrderListResponse>>();
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const SHIPMENT_MUTATION_TIMEOUT_MS = 120_000;

export class OMSRequestError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly operationId?: string,
    public readonly state?: ShipmentMutationState,
    /** The refusal's JSON body, for a caller that reads more than the message (the order cancel's CANCEL_LOCKED). */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'OMSRequestError';
  }
}

export class OMSClient {
  // `apiUrl` routes /orders/* to internal-api; `fulfilmentApiUrl` keeps inbound + fulfilment on facade (not yet on prod internal-api).
  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
    private readonly fulfilmentApiUrl: string,
  ) {}

  /**
   * Single-page fetch. Returns one page exactly as the backend sends it, with the opaque
   * `lastEvaluatedKey` cursor (undefined when there is no next page). No exhaust loop and no
   * client-side dedupe — the caller owns cursor history and pagination. Use this for the list view.
   */
  public async listOrdersPage(params: OrderListParams): Promise<OrderListResponse> {
    const requestParams = this.normalizeOrderListParams(params);
    const queryString = this.buildQueryString(requestParams);
    const url = `${this.getApiBaseUrl()}/orders?${queryString}`;
    const existing = listOrdersPageRequests.get(url);
    if (existing) {
      return existing;
    }

    const request = (async () => {
      return this.fetchOrderListPage(requestParams);
    })().finally(() => {
      listOrdersPageRequests.delete(url);
    });

    listOrdersPageRequests.set(url, request);
    return request;
  }

  /**
   * Exhaust path: walks every page for the query and returns the deduplicated full set.
   * Kept for the export modals; do not use for the paginated list view.
   */
  public async listOrders(
    params: OrderListParams,
    onPage?: (pageOrders: OrderSummary[]) => void,
  ): Promise<OrderListResponse> {
    const allOrders: OrderSummary[] = [];
    let lastEvaluatedKey: string | undefined = params.lastEvaluatedKey;

    do {
      const requestParams: OrderListParams = { ...params };
      if (lastEvaluatedKey) {
        requestParams.lastEvaluatedKey = lastEvaluatedKey;
      }

      const data = await this.fetchOrderListPage(requestParams);
      allOrders.push(...data.orders);
      onPage?.(data.orders);
      // Opaque cursor: serialize the response object and hand it straight back next iteration.
      lastEvaluatedKey = data.lastEvaluatedKey ? JSON.stringify(data.lastEvaluatedKey) : undefined;
    } while (lastEvaluatedKey);

    // Deduplicate orders by keeping only the latest version of each orderId
    const orderMap = new Map<string, OrderSummary>();
    for (const order of allOrders) {
      const existing = orderMap.get(order.orderId);
      if (!existing || new Date(order.updatedAt) > new Date(existing.updatedAt)) {
        orderMap.set(order.orderId, order);
      }
    }
    const deduplicatedOrders = Array.from(orderMap.values());

    return { orders: deduplicatedOrders };
  }

  public async getOrderDetail(orderId: string): Promise<OrderSummary | null> {
    const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}`, {
      headers: { 'X-API-Key': this.apiKey },
    });
    if (!response.ok) {
      console.error(`Failed to fetch order detail: ${response.status} ${response.statusText}`);
      return null;
    }
    return await response.json();
  }

  public async updateOrder(orderId: string, request: OrderUpdateRequest): Promise<OrderSummary | null> {
    const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.apiKey,
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      let errorMessage = `Failed to update order: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } catch {
        // If parsing fails, use the default error message
      }
      console.error(errorMessage);
      throw new Error(errorMessage);
    }
    return await this.getOrderDetail(orderId);
  }

  public async updateOrderStatus(orderId: string, status: string, operator: string): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify({
          status,
          auditEntry: {
            source: 'OPERATOR',
            operator: operator,
          },
        }),
      });

      if (!response.ok) {
        console.error(`Failed to update order status: ${response.status} ${response.statusText}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  async requestInbound(orderId: string, inboundRequest: InboundRequest): Promise<InboundResponse> {
    try {
      console.debug('inboundRequest', inboundRequest);
      const response = await fetch(`${this.getFulfilmentBaseUrl()}/orders/${orderId}/inbound`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inboundRequest),
      });

      if (!response.ok) {
        let errorMessage = `Failed to request inbound: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch {
          // If parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error requesting inbound:', error);
      throw error;
    }
  }

  public async getStockStatus(skus: string): Promise<StockStatusResponse> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/stockstatus?skus=${skus}`, {
        headers: { 'X-API-Key': this.apiKey },
      });
      if (!response.ok) {
        throw new Error(`Failed to get stock status: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting stock status:', error);
      throw error;
    }
  }

  public async createDeleoOrder(orderId: string, reason: string): Promise<OrderSummary | null> {
    return (await this.postApiKeyMutation<OrderSummary>(`/orders/${orderId}/deleo`, { reason })) ?? null;
  }

  public async createAutoRoutedShipment(orderId: string, reason: string): Promise<OrderSummary | null> {
    return this.createShipment(orderId, { reason });
  }

  public async createShipment(orderId: string, request: CreateShipmentRequest): Promise<OrderSummary | null> {
    await this.postApiKeyMutation<OrderSummary>(`/orders/${orderId}/shipment`, request);
    return this.getOrderDetail(orderId);
  }

  public async resetDeleoOrder(orderId: string, reason: string): Promise<OrderSummary | null> {
    await this.postApiKeyMutation<OrderSummary>(`/orders/${orderId}/deleo/reset`, { reason });
    return this.getOrderDetail(orderId);
  }

  public async resetParxlOrder(orderId: string, reason: string): Promise<OrderSummary | null> {
    await this.postApiKeyMutation<OrderSummary>(`/orders/${orderId}/deleo/reset`, { reason });
    return this.getOrderDetail(orderId);
  }

  public async resetYunOrder(orderId: string, reason: string): Promise<OrderSummary | null> {
    await this.postApiKeyMutation<OrderSummary>(`/orders/${orderId}/deleo/reset`, { reason });
    return this.getOrderDetail(orderId);
  }

  // Re-fetches provider tracking for the order's current providerOrderId and
  // refreshes shippingData/yunExpressData/status. Note: a 200 response with
  // success=false means the provider could not resolve the tracking number;
  // callers must check the `success` field, not just the HTTP status.
  public async refreshShippingStatus(orderId: string): Promise<RefreshShippingStatusResponse> {
    const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/shippingStatus/refresh`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.apiKey,
      },
    });
    if (!response.ok) {
      let errorMessage = `Failed to refresh shipping status: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If parsing fails, use the default error message
      }
      throw new Error(errorMessage);
    }
    return (await response.json()) as RefreshShippingStatusResponse;
  }

  async getDeliveryTrace(orderId: string): Promise<TraceResponse> {
    try {
      console.debug('getDeliveryTrace', orderId);
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/trace`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch delivery trace: ${response.status} ${response.statusText}`);
        return { orderId, status: { name: '', code: '', desc: '' }, traceInfoList: [] };
      }

      const data = await response.json();
      console.debug('getDeliveryTrace', data);
      return data;
    } catch (error) {
      console.error('Error fetching delivery trace:', error);
      return { orderId, status: { name: '', code: '', desc: '' }, traceInfoList: [] };
    }
  }

  public async cancelInbound(
    orderId: string,
    inboundId: string,
    auditEntry: AuditHistoryEntryInput,
  ): Promise<InboundResponse | null> {
    try {
      const response = await fetch(`${this.getFulfilmentBaseUrl()}/orders/${orderId}/inbound/${inboundId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ auditEntry }),
      });

      if (!response.ok) {
        let errorMessage = `Failed to cancel inbound: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch {
          // If parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }
      return await response.json();
    } catch (error) {
      console.error('Error canceling inbound:', error);
      throw error;
    }
  }

  /**
   * Cancels an order through the order route (OMS Center, and Order Detail for a web order). The cancel type and the
   * time the customer asked (SSD-7450) ride in the body only when given, so an older server reads the request as
   * before. A refusal throws an OMSRequestError carrying the status, the code and the JSON body, with the message as
   * it always was; a locked change of mind answers 409 with code CANCEL_LOCKED and the lock's time and source.
   */
  public async cancelOrder(
    orderId: string,
    cancelInfo: OrderCancelInfo,
    auditEntry: AuditHistoryEntryInput,
  ): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify({
          reason: cancelInfo.reason,
          operator: cancelInfo.operator,
          auditEntry: auditEntry,
          ...(cancelInfo.cancelType === undefined ? {} : { cancelType: cancelInfo.cancelType }),
          ...(cancelInfo.customerRequestedAt === undefined
            ? {}
            : { customerRequestedAt: cancelInfo.customerRequestedAt }),
          ...(cancelInfo.lockOverrideReason === undefined ? {} : { lockOverrideReason: cancelInfo.lockOverrideReason }),
          ...(cancelInfo.goodsNote === undefined ? {} : { goodsNote: cancelInfo.goodsNote }),
          ...(cancelInfo.lines === undefined ? {} : { lines: cancelInfo.lines }),
        }),
      });
      if (!response.ok) {
        let errorMessage = `Failed to cancel order: ${response.statusText}`;
        let errorData: unknown;
        try {
          errorData = await response.json();
          const data = errorData as { message?: unknown; error?: unknown } | null;
          if (data?.message) {
            errorMessage = String(data.message);
          } else if (data?.error) {
            errorMessage = String(data.error);
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch {
          // If parsing fails, use the default error message
        }
        const code = (errorData as { code?: unknown } | undefined)?.code;
        throw new OMSRequestError(
          errorMessage,
          response.status,
          typeof code === 'string' ? code : undefined,
          undefined,
          undefined,
          errorData,
        );
      }
      return await response.json();
    } catch (error) {
      console.error('Error canceling order:', error);
      throw error;
    }
  }

  public async listAuditHistory(orderId: string): Promise<AuditHistoryEntry[]> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/audit`, {
        headers: { 'X-API-Key': this.apiKey },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch audit history: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching audit history:', error);
      throw error;
    }
  }

  public async cloneOrder(cloneOrderRequest: CloneOrderRequest): Promise<OrderSummary | null> {
    return (await this.postApiKeyMutation<OrderSummary>('/orders/clone', cloneOrderRequest)) ?? null;
  }

  public async switchShipmentProvider(
    orderId: string,
    request: SwitchShipmentProviderRequest,
  ): Promise<SwitchShipmentProviderResponse> {
    const result = await this.postApiKeyMutation<SwitchShipmentProviderResponse>(
      `/orders/${orderId}/shippingProviderSwitch`,
      request,
    );
    if (!result) {
      throw new OMSRequestError(
        'The provider switch returned no operation result. Refresh and reconcile the order before retrying.',
        502,
        'SHIPMENT_RESPONSE_INVALID',
      );
    }
    return result;
  }

  public async mergeOrders(mergeOrdersRequest: MergeOrdersRequest): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify(mergeOrdersRequest),
      });
      if (!response.ok) {
        let errorMessage = `Failed to merge orders: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch {
          // If parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }
      return await response.json();
    } catch (error) {
      console.error('Error merging orders:', error);
      throw error;
    }
  }

  public async getOrderByTracking(trackingNo: string): Promise<OrderSummary[]> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/tracking/${trackingNo}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order by tracking: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching order by tracking:', error);
      throw error;
    }
  }

  public async searchWmsOrders(params: WmsOrderSearchParams): Promise<OrderSummary[]> {
    const queryString = new URLSearchParams();
    if (params.trackingNumber) {
      queryString.append('trackingNumber', params.trackingNumber);
    }
    if (params.inboundTrackingOrderNumber) {
      queryString.append('inboundTrackingOrderNumber', params.inboundTrackingOrderNumber);
    }
    if (!queryString.toString()) {
      throw new Error('At least one WMS search filter is required');
    }

    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/wms/search?${queryString}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (response.status === 404) {
        return [];
      }
      if (!response.ok) {
        throw new Error(`Failed to search WMS orders: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching WMS orders:', error);
      throw error;
    }
  }

  public async getFulfillmentStatus(): Promise<OrderFulfillmentStatusListResponse> {
    try {
      const response = await fetch(`${this.getFulfilmentBaseUrl()}/fulfilment/status`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch fulfillment status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching fulfillment status:', error);
      throw error;
    }
  }

  public async createOrder(orderId: string): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify({ id: orderId }),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create order: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch {
          // If parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  private async postApiKeyMutation<T>(path: string, body: unknown): Promise<T | undefined> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), SHIPMENT_MUTATION_TIMEOUT_MS);
    try {
      const response = await fetch(`${this.getApiBaseUrl()}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const responseText = await response.text();

      if (!response.ok) {
        let errorData: unknown;
        try {
          errorData = responseText.trim() ? JSON.parse(responseText) : undefined;
        } catch {
          throw new OMSRequestError(
            `Shipment operation failed: ${response.status} ${response.statusText}`,
            response.status,
          );
        }

        const payload =
          typeof errorData === 'object' && errorData !== null ? (errorData as Record<string, unknown>) : undefined;
        const rawMessage = payload?.['message'] ?? payload?.['error'];
        const message = Array.isArray(rawMessage)
          ? rawMessage.join(', ')
          : typeof rawMessage === 'string'
            ? rawMessage
            : typeof errorData === 'string'
              ? errorData
              : `Shipment operation failed: ${response.status} ${response.statusText}`;

        throw new OMSRequestError(
          message,
          response.status,
          typeof payload?.['code'] === 'string' ? payload['code'] : undefined,
          typeof payload?.['operationId'] === 'string' ? payload['operationId'] : undefined,
          typeof payload?.['state'] === 'string' ? (payload['state'] as ShipmentMutationState) : undefined,
        );
      }

      if (!responseText.trim()) {
        return undefined;
      }
      try {
        return JSON.parse(responseText) as T;
      } catch {
        throw new OMSRequestError(
          'The shipment operation returned an invalid response. Refresh and reconcile the order before retrying.',
          502,
          'SHIPMENT_RESPONSE_INVALID',
        );
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new OMSRequestError(
          'The shipment operation timed out before a result was received. Refresh and reconcile the order before retrying.',
          408,
          'SHIPMENT_REQUEST_TIMEOUT',
        );
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private getApiBaseUrl(): string {
    return this.apiUrl;
  }

  private getFulfilmentBaseUrl(): string {
    return this.fulfilmentApiUrl;
  }

  private async fetchOrderListPage(params: OrderListParams): Promise<OrderListResponse> {
    const response = await fetch(`${this.getApiBaseUrl()}/orders?${this.buildQueryString(params)}`, {
      headers: { 'X-API-Key': this.apiKey },
    });

    if (response.ok) {
      return (await response.json()) as OrderListResponse;
    }

    if (response.status === 400 && params.startDate && params.endDate) {
      // One retry keeps admin compatible during /orders date-range rollout; remove after BigQuery API is fully deployed.
      const legacyResponse = await fetch(`${this.getApiBaseUrl()}/orders?${this.buildQueryString(params, true)}`, {
        headers: { 'X-API-Key': this.apiKey },
      });

      if (legacyResponse.ok) {
        return (await legacyResponse.json()) as OrderListResponse;
      }

      console.error(`Failed to fetch orders fallback: ${legacyResponse.status} ${legacyResponse.statusText}`);
    }

    console.error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
    return { orders: [] };
  }

  private normalizeOrderListParams(params: OrderListParams): OrderListParams {
    const hasStartDate = Boolean(params.startDate);
    const hasEndDate = Boolean(params.endDate);
    if (hasStartDate === hasEndDate) {
      return params;
    }

    console.warn('Ignoring incomplete order date range; provide both startDate and endDate.');
    const withoutDateRange = { ...params };
    delete withoutDateRange.startDate;
    delete withoutDateRange.endDate;
    return withoutDateRange;
  }

  private buildQueryString(params: OrderListParams, legacyDateRange = false): string {
    const queryString = new URLSearchParams();
    // Query mode: a startDate+endDate range takes precedence and makes orderMonth redundant.
    if (params.startDate && params.endDate) {
      const range =
        legacyDateRange && DATE_ONLY.test(params.startDate) && DATE_ONLY.test(params.endDate)
          ? sgtRangeToInstantBounds(params.startDate, params.endDate)
          : undefined;
      queryString.append('startDate', range?.from ?? params.startDate);
      queryString.append('endDate', range?.to ?? params.endDate);
    } else if (params.orderMonth) {
      queryString.append('orderMonth', params.orderMonth);
    }
    if (params.countryCode) {
      queryString.append('countryCode', params.countryCode);
    }
    if (params.status) {
      queryString.append('status', params.status);
    }
    if (params.orderDate) {
      queryString.append('orderDate', params.orderDate);
    }
    if (params.search && !legacyDateRange) {
      queryString.append('search', params.search);
    }
    if (params.pic && !legacyDateRange) {
      queryString.append('pic', params.pic);
    }
    if (params.hasOrderNumber && !legacyDateRange) {
      queryString.append('hasOrderNumber', 'true');
    }
    if (params.hasTrackingNumber && !legacyDateRange) {
      queryString.append('hasTrackingNumber', 'true');
    }
    if (params.limit) {
      queryString.append('limit', params.limit.toString());
    }
    if (params.lastEvaluatedKey) {
      const serializedKey =
        typeof params.lastEvaluatedKey === 'object' ? JSON.stringify(params.lastEvaluatedKey) : params.lastEvaluatedKey;
      queryString.append('lastEvaluatedKey', serializedKey);
    }
    return queryString.toString();
  }
}
